import classNames from 'classnames/bind';
import styles from './Library.module.css';
import { useEffect, useState, useRef } from 'react';
import {
  ACTIVITY_LIBRARIES,
  EBOOK_ACTIVITY_DELETE,
  EMPTY_EBOOK_LIST,
  LIBRARY,
  SEARCH_RESULT_NOT_FOUND,
  SHARED_EBOOK_BY_ME,
  SHARED_LIBRARY,
} from '../../../constants';
import { LibraryType, ProjectList } from '../../../types/analysis';
import { useAppDispatch } from '../../../hooks/useRedux';
import Search from '../../../components/Search';
import Slider from '../../../components/Slider';
import Select from '../../../components/Select';
import EbookActivityDetails from '../EbookActivityDetails';
import useModal from '../../../hooks/useModal';
import { LazyQueryTrigger } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';

interface Library {
  type: LibraryType;
  projects: {
    queryTrigger: LazyQueryTrigger<any>;
    isSuccess: boolean;
    projectLibraryChanged?: ActionCreatorWithPayload<LibraryType>;
    projectListChanged: ActionCreatorWithPayload<ProjectList>;
    projectIdChanged: ActionCreatorWithPayload<number>;
  };
  projectId: number;
  userId?: number;
}

export default function Library({
  type,
  projects: {
    queryTrigger,
    isSuccess,
    projectLibraryChanged,
    projectListChanged,
    projectIdChanged,
  },
  projectId,
  userId,
}: Library) {
  const cx = classNames.bind(styles);
  const dispatch = useAppDispatch();

  const [libraryType, setLibraryType] = useState(type);
  const [searchText, setSearchText] = useState('');
  const [projectList, setProjectList] = useState<ProjectList>([]);

  const dashSectionRef = useRef<HTMLDivElement>(null);

  const { toggleModal } = useModal();

  const libraryTypeList = [
    'library',
    'share',
    ...(userId ? ['sharedByMe'] : []),
  ];

  const getProjectList = async () => {
    try {
      const { data } = await queryTrigger({
        key: libraryTypeList.indexOf(libraryType),
        search: searchText,
        sortBy: 'last_date',
        sortDir: 'DESC',
        userId,
      });
      const projectListData = data as ProjectList;

      setProjectList(projectListData);
      dispatch(projectListChanged(projectListData));

      if (projectListData && projectListData.length > 0 && projectId === 0) {
        dispatch(projectIdChanged(projectListData[0].projectId));
      }
    } catch (error) {
      console.error('Fail to get projects', error);
    }
  };

  const changeLibraryType = (value: string) => {
    const type = {
      [LIBRARY]: 'library',
      [SHARED_LIBRARY]: 'share',
      [SHARED_EBOOK_BY_ME]: 'sharedByMe',
    }[value] as LibraryType;

    setLibraryType(type);
    setSearchText('');
    dispatch(projectIdChanged(0));

    if (!userId && projectLibraryChanged) {
      dispatch(projectLibraryChanged(type));
    }
  };

  const submitSearch = (keyword: string) => {
    setSearchText(keyword);
    dispatch(projectIdChanged(0));
  };

  const deleteProjectActivity = () => {
    toggleModal({
      type: 'confirm',
      id: 'confirmDeleteActivity',
      isOpen: true,
      data: { projectId },
    });
  };

  useEffect(() => {
    getProjectList();
  }, [libraryType, searchText]);

  useEffect(() => {
    dispatch(projectIdChanged(0));
  }, []);

  if (!isSuccess) return null;

  return (
    <div className={cx('dash_section')} ref={dashSectionRef}>
      <div className={cx('dash_option')}>
        <div className={cx('option_select')}>
          <i className={cx('svg_icon', `ico_${libraryType}`)}></i>
          <Select
            name="library"
            list={[
              userId ? SHARED_EBOOK_BY_ME : '',
              LIBRARY,
              SHARED_LIBRARY,
            ].filter(Boolean)}
            value={ACTIVITY_LIBRARIES[libraryType]}
            onClick={changeLibraryType}
          />
        </div>
        <Search onSubmit={submitSearch} />
      </div>

      <div className={cx('dash_book_list')}>
        {projectList.length ? (
          <Slider
            name="activity-ebook-list"
            list={projectList}
            dashSectionRef={dashSectionRef}
          />
        ) : (
          <div className={cx('empty_upload')}>
            <p className={cx('desc')}>
              {searchText ? SEARCH_RESULT_NOT_FOUND : EMPTY_EBOOK_LIST}
            </p>
          </div>
        )}
      </div>

      {!!projectId && (
        <>
          <EbookActivityDetails projectId={projectId} userId={userId} />
          {!userId && (
            <div className={cx('btn_wrap')}>
              <button
                className={cx('btn', 'skyt', 'del_activity', 'each')}
                onClick={deleteProjectActivity}
              >
                <span>{EBOOK_ACTIVITY_DELETE}</span>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
